// Used for MLB, NBA, NFL
import React, { useState, useEffect, useRef } from 'react'
import { Box, RadioButtonGroup, Text, Heading } from 'grommet'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { device } from '../../theme/device'
import { useHideInput } from '../../contextHook/hideInput'
import InputButton from '../Button/InputButton'
import HideSortTitleButton from '../Button/HideSortTitleButton'
import TextButton from '../Button/TextButton'
import { intersectionBy } from '../../utils'
import { usePersistValue } from '../../hook/usePersistValue'
import { useDarkMode } from '../../contextHook/darkMode'
import InputLength from '../Common/InputLength'
import { preloadImage } from '../../utils/imagePreloader'
import ListItem from './ListItem'

const Container = styled(Box)`
  border-radius: 2rem;
  box-shadow: 0px 0px 2px 1px ${props => props.theme.global.colors.border};
  min-height: 0px;
  padding: 0.8rem 2rem 1rem;
  width: 50rem;
  background-color: ${props => props.theme.global.colors.baseBackground};
  @media ${device.medium} {
    padding: 0.8rem;
  }
`

const OptionsContainer = styled(Box)`
  &::-webkit-scrollbar {
    width: 5px !important;
  }
  &::-webkit-scrollbar-track {
    background: transparent !important;
  }
  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.global.colors.scrollThumb} !important;
    border-radius: 3px;
  }
`

const ReactSports = ({
  page,
  conferenceList,
  allTeams,
  category = 'Conference',
}) => {
  const { value: conferenceActive } = usePersistValue(
    state => state[page].conferenceActive,
    'AllTeams',
  )
  const { value: teamsActive } = usePersistValue(
    state => state[page].teamsActive,
    allTeams,
  )
  const [teamOptions, setTeamOptions] = useState([])
  const { value: choices } = usePersistValue(state => state[page].choices, [])
  const { value: showMode } = usePersistValue(
    state => state[page].showMode,
    'Logo & Name',
  )
  const { value: toolColors } = usePersistValue(
    state => state.settings.wheelColors.colors,
    ['', '', '', ''],
  )
  const { value: darkMode } = useDarkMode()
  const { setHiding } = useHideInput()
  const dispatch = useDispatch()
  const manualChangeConference = useRef(false)

  useEffect(() => {
    if (!manualChangeConference.current) {
      updateOptions(conferenceActive) // only trigger when first hydrate and also when share copy/back to the wheel.
    } else {
      manualChangeConference.current = false
    }
  }, [conferenceActive])

  const setChoices = newChoices => {
    dispatch({
      type: `SET_CHOICES_${page.toUpperCase()}`,
      payload: { choices: newChoices, nullTurntable: true },
    })
  }

  const setTeamsActive = newTeamsActive => {
    dispatch({
      type: `SET_TEAMS_ACTIVE_${page.toUpperCase()}`,
      payload: { teamsActive: newTeamsActive },
    })
  }

  const setConferenceActive = newConferenceActive => {
    dispatch({
      type: `SET_CONFERENCE_ACTIVE_${page.toUpperCase()}`,
      payload: { conferenceActive: newConferenceActive },
    })
  }

  const setShowMode = newShowMode => {
    dispatch({
      type: `SET_SHOW_MODE_${page.toUpperCase()}`,
      payload: { showMode: newShowMode },
    })
  }

  const changeConference = key => {
    setConferenceActive(key)
    const options = updateOptions(key)
    updateValues(key, options)
  }

  const preloadImages = teams => {
    const promises = teams.map(team => preloadImage(team.data))
    return Promise.all(promises)
  }

  const updateOptions = tempConferenceActive => {
    let options = conferenceList[tempConferenceActive].dataList
    preloadImages(options)
    options.sort((a, b) => a.name.localeCompare(b.name))
    setTeamOptions(options)
    return options
  }

  const updateValues = (key, options) => {
    let values = teamsActive.slice(0)
    values = intersectionBy(values, options, (v, u) => v.name === u.name)
    setChoices(values)
  }

  const handleSelectAll = () => {
    if (choices.length === 0) {
      // Select All
      setChoices(teamOptions)
      const values = teamsActive.concat(
        teamOptions.filter(
          option => !teamsActive.some(team => team.name === option.name),
        ),
      )
      setTeamsActive(values)
    } else {
      // Clear All
      setChoices([])
      const values = teamsActive.filter(
        option => !teamOptions.some(team => team.name === option.name),
      )
      setTeamsActive(values)
    }
  }

  return (
    <Container justify="start" gap=".5rem">
      <Box align="center" gap=".7rem" direction="row" justify="between">
        <Box direction="row" align="center" gap=".6rem">
          <Heading level={3} alignSelf="center" margin={{ vertical: '0px' }}>
            INPUTS
          </Heading>
          <InputLength
            length={
              choices.length === teamOptions.length
                ? teamOptions.length
                : `${choices.length}/${teamOptions.length}`
            }
          />
        </Box>
        <HideSortTitleButton
          setHiding={setHiding}
          choices={choices}
          setChoices={newChoices => {
            return {
              type: `SET_CHOICES_${page.toUpperCase()}`,
              payload: { choices: newChoices, nullTurntable: true },
            }
          }}
        />
      </Box>
      <Box align="stretch" gap=".5rem">
        <Text>Select {category} & Insert Team(s):</Text>
        <Box direction="row" wrap gap=".8rem" justify="center">
          {Object.keys(conferenceList).map(key => {
            return (
              <InputButton
                active={conferenceActive === key}
                onClick={() => changeConference(key)}
              >
                {conferenceList[key].name}
              </InputButton>
            )
          })}
        </Box>

        <Box
          margin=".3rem 0 .5rem"
          pad={{ vertical: '0.5rem' }}
          height="38rem"
          border={{ size: '1.5px', side: 'all', color: 'border' }}
          round={{ size: '.5rem' }}
        >
          <Box
            direction="row"
            justify="between"
            pad={{
              horizontal: '1rem',
            }}
            height={{ min: '2.4rem' }}
            align="center"
          >
            <Text size="1.4rem">
              {`${choices.length} selected of ${teamOptions.length}`} teams
            </Text>
            <TextButton
              onClick={handleSelectAll}
              label={choices.length === 0 ? 'Select All' : 'Clear All'}
              color="text"
              hoverStyles={{ textDecoration: 'none' }}
            />
          </Box>
          <OptionsContainer
            overflow={{ horizontal: 'hidden', vertical: 'auto' }}
            margin={{ right: '.2rem' }}
          >
            {conferenceList[conferenceActive].divisionList.map(division => (
              <>
                <Text
                  style={{ textDecoration: 'underline' }}
                  margin={{ left: '1rem', top: '1rem' }}
                >
                  {conferenceActive === 'AllTeams'
                    ? division
                    : division.split(' ').slice(1).join(' ')}
                </Text>
                {teamOptions
                  .filter(team => team.division === division)
                  .map(team => (
                    <ListItem
                      key={team.name}
                      item={team}
                      selectedItems={teamsActive}
                      availableItems={teamOptions}
                      setSelectedItems={setTeamsActive}
                      setAllSelectedItems={setChoices}
                    />
                  ))}
              </>
            ))}
          </OptionsContainer>
        </Box>

        <Box gap=".5rem">
          <Text>Show:</Text>
          <RadioButtonGroup
            direction="row"
            justify="evenly"
            name="showMode"
            toolColor={
              !darkMode || toolColors.length < 3 ? toolColors[0] : toolColors[2]
            }
            options={['Logo & Name', 'Logo', 'Name']}
            value={showMode}
            onChange={event => {
              setShowMode(event.target.value)
            }}
            background="baseBackground"
          />
        </Box>
      </Box>
    </Container>
  )
}

export default ReactSports
